import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building,
  User,
  Mail,
  Phone,
  Globe,
  Eye,
  Loader2
} from "lucide-react";
import { ClientReferralWithUI } from "@/api/clientReferrals";
import { formatRewardAmount, formatDateForSouthAfrica } from "@/lib/textUtils";

interface ClientReferralKanbanCardItemProps {
  referral: ClientReferralWithUI;
  onViewReferral: (referral: ClientReferralWithUI) => void;
  onDragStart?: (e: React.DragEvent, referralId: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  isSyncing?: boolean;
  className?: string;
  showActions?: boolean;
  showReferrer?: boolean;
  showDate?: boolean;
}

export function ClientReferralKanbanCardItem({
  referral,
  onViewReferral,
  onDragStart,
  onDragEnd,
  isDragging = false,
  isSyncing = false,
  className = "",
  showActions = true,
  showReferrer = true,
  showDate = true
}: ClientReferralKanbanCardItemProps) {
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-4 border-l-red-500';
      case 'medium': return 'border-l-4 border-l-yellow-500';
      case 'low': return 'border-l-4 border-l-green-500';
      default: return 'border-l-4 border-l-gray-500';
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (onDragStart) {
      onDragStart(e, referral.id);
    }
  };

  return (
    <Card
      className={`border-0 shadow-sm cursor-move hover:shadow-lg transition-all duration-200 ease-out ${getPriorityColor(referral.priority)} ${
        isDragging 
          ? 'opacity-50 scale-95 rotate-2 shadow-2xl z-50' 
          : 'hover:scale-[1.02] hover:-translate-y-1'
      } ${isSyncing ? 'animate-pulse' : ''} ${className}`}
      draggable={!!onDragStart && !isSyncing}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      data-testid={`client-referral-card-${referral.id}`}
    >
      <CardContent className="p-4">
        {/* Header: Company Name and Priority */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 space-grotesk text-sm lg:text-base truncate">
              {referral.companyName}
            </h4>
            <div className="flex items-center gap-2 mt-1">
              <User className="h-3 w-3 text-gray-400" />
              <p className="text-xs text-gray-600 space-grotesk truncate">
                {referral.pointOfContact}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {isSyncing && (
              <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2">
            <Mail className="h-3 w-3 text-gray-400" />
            <p className="text-xs text-gray-600 space-grotesk truncate">
              {referral.email}
            </p>
          </div>
          
          {referral.phoneNumber && (
            <div className="flex items-center gap-2">
              <Phone className="h-3 w-3 text-gray-400" />
              <p className="text-xs text-gray-600 space-grotesk">
                {referral.phoneNumber}
              </p>
            </div>
          )}
          
          {referral.website && (
            <div className="flex items-center gap-2">
              <Globe className="h-3 w-3 text-gray-400" />
              <p className="text-xs text-blue-600 space-grotesk truncate">
                {referral.website}
              </p>
            </div>
          )}
        </div>

        {/* Reward Amount */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 space-grotesk">Reward</span>
            <span className="text-sm font-semibold space-grotesk">
              R{formatRewardAmount(referral.rewardAmount)}
            </span>
          </div>
        </div>

        {/* Referrer Information */}
        {showReferrer && (
          <div className="mb-3 p-2 bg-gray-50 rounded-md">
            <div className="flex items-center gap-2">
              <Building className="h-3 w-3 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 space-grotesk truncate">
                  {referral.referrerName}
                </p>
                <p className="text-xs text-gray-500 space-grotesk truncate">
                  {referral.referrerEmail}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Date Information */}
        {showDate && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-500 space-grotesk">
              <span>Created</span>
              <span>{formatDateForSouthAfrica(referral.createdAt)}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewReferral(referral)}
              className="text-xs space-grotesk h-7 px-2"
              data-testid={`view-client-referral-${referral.id}`}
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
